import { parseNumber } from '../lib/text-answers';
/**
 * Страница игры модуля образовательной коллекции
 * apps/web/src/pages/CollectionPlayPage.tsx
 * 
 * v2.2 - Compact design with gamification & report modal
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { shareResult } from '../lib/share';
import { detectPlatform, isCoursesSite } from '../platform/detect';
import { useParams, useNavigate } from 'react-router-dom';
import { useCollectionsStore } from '../stores/collections';
import { answerQuestion as apiAnswerQuestion, type HotspotZone, type LineOption } from '../lib/collections-api';
import { coursesBotUrl } from '../lib/bots';

// ==================== Animations CSS ====================
const animationStyles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes bounce {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.08); }
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }
  @keyframes confetti {
    0% { transform: translateY(0) rotate(0deg); opacity: 1; }
    100% { transform: translateY(-80px) rotate(720deg); opacity: 0; }
  }
  @keyframes streakFire {
    0%, 100% { transform: scale(1) rotate(-3deg); }
    50% { transform: scale(1.15) rotate(3deg); }
  }
  .animate-fadeInUp { animation: fadeInUp 0.3s ease-out; }
  .animate-scaleIn { animation: scaleIn 0.25s ease-out; }
  .animate-bounce { animation: bounce 0.35s ease-out; }
  .animate-shake { animation: shake 0.35s ease-out; }
  .animate-streakFire { animation: streakFire 0.4s ease-in-out infinite; }
`;

// ==================== Confetti Component ====================
function Confetti({ show }: { show: boolean }) {
  if (!show) return null;
  
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.2,
    color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][i % 5],
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute w-2 h-2 rounded-full"
          style={{
            left: `${p.left}%`,
            top: '40%',
            backgroundColor: p.color,
            animation: `confetti 0.8s ease-out ${p.delay}s forwards`,
          }}
        />
      ))}
    </div>
  );
}

// ==================== Report Modal ====================
function ReportModal({ 
  isOpen, 
  questionText, 
  collectionId, 
  moduleId, 
  userId, 
  onClose 
}: {
  isOpen: boolean;
  questionText: string;
  collectionId?: string;
  moduleId?: string;
  userId?: number;
  onClose: () => void;
}) {
  const [reportType, setReportType] = useState('wrong_answer');
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  // Жалоба уходит в «Обращения» дашборда (08.09.2026); «Спасибо» показываем только когда сервер её принял
  const handleSubmit = async () => {
    setSending(true); setError('');
    try {
      let webUserId = '';
      try { webUserId = localStorage.getItem('beshek_uid') || ''; } catch { /* нет localStorage */ }
      const res = await fetch('/api/collections/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionText, collectionId, moduleId, reportType, comment, userId, site: isCoursesSite() ? 'kurs' : 'beshek', webUserId }),
      });
      if (!res.ok) { setError('Не удалось отправить, попробуйте позже'); return; }
      setSent(true);
      setTimeout(() => {
        onClose();
        setSent(false);
        setComment('');
      }, 1500);
    } catch (e) {
      console.error(e);
      setError('Не удалось отправить, попробуйте позже');
    } finally {
      setSending(false); 
    }
  };

  const types = [
    { id: 'wrong_answer', label: '❌ Неправильный ответ', desc: 'Указан неверный правильный ответ' },
    { id: 'typo', label: '📝 Опечатка', desc: 'Ошибка в тексте' },
    { id: 'unclear', label: '❓ Непонятно', desc: 'Вопрос неясен' },
    { id: 'other', label: '💬 Другое', desc: 'Другая проблема' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-2xl p-4 shadow-xl animate-scaleIn" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="text-center py-6">
            <div className="text-5xl mb-3">✅</div>
            <p className="font-semibold text-gray-900">Спасибо!</p>
            <p className="text-sm text-[#626B73]">Сообщение получено, исправим</p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-gray-900">🚩 Сообщить об ошибке</h3>
              <button onClick={onClose} className="text-[#626B73] hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="space-y-2 mb-3">
              {types.map((t) => (
                <button 
                  key={t.id} 
                  onClick={() => setReportType(t.id)}
                  className={`w-full p-2 rounded-lg text-left text-sm transition-all ${
                    reportType === t.id 
                      ? 'bg-[#E5F2EF] border-2 border-[#0F766E]' 
                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <span className="font-medium">{t.label}</span>
                  <p className="text-xs text-[#626B73]">{t.desc}</p>
                </button>
              ))}
            </div>
            <textarea 
              value={comment} 
              onChange={(e) => setComment(e.target.value)} 
              placeholder="Опишите проблему (необязательно)"
              className="w-full p-2 border rounded-lg text-sm resize-none h-16 mb-3" 
            />
            <button 
              onClick={handleSubmit} 
              disabled={sending}
              className="w-full py-2.5 bg-[#0F766E] hover:bg-[#0B5F59] disabled:opacity-50 text-white font-semibold rounded-lg"
            >
              {sending ? 'Отправка...' : 'Отправить'}
            </button>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <p className="mt-3 text-xs text-[#626B73] text-center">
              Не ошибка, а вопрос по теме?{' '}
              <a href={`/support?from=${encodeURIComponent(window.location.href)}`} className="text-[#0F766E] underline">Задать вопрос</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ==================== Progress Header ====================
function ProgressHeader({ 
  current, 
  total,
  streak,
  xpEarned,
  onExit 
}: { 
  current: number; 
  total: number;
  streak: number;
  xpEarned: number;
  onExit?: () => void;
}) {
  const percent = total > 0 ? (current / total) * 100 : 0;

  return (
    <div className="bg-[#0F766E] px-3 py-2 text-white">
      <style>{animationStyles}</style>
      
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={onExit}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          aria-label="Выйти"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
          <span className="text-sm font-medium">{current}</span>
          <span className="text-white/80">/</span>
          <span className="text-sm text-white/80">{total}</span>
        </div>

        <div className="flex items-center gap-2">
          {streak > 0 && (
            <div className={`flex items-center gap-1 bg-orange-500/90 rounded-full px-2 py-0.5 animate-scaleIn ${streak >= 3 ? 'animate-streakFire' : ''}`}>
              <span>🔥</span>
              <span className="text-xs font-bold">{streak}</span>
            </div>
          )}
          {xpEarned > 0 && (
            <div className="flex items-center gap-1 bg-yellow-500/90 rounded-full px-2 py-0.5">
              <span className="text-xs">⭐</span>
              <span className="text-xs font-bold">+{xpEarned}</span>
            </div>
          )}
        </div>
      </div>

      <div className="h-1.5 bg-white/30 rounded-full overflow-hidden">
        <div
          className="h-full bg-white rounded-full transition-all duration-500 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

// ==================== Question Card ====================
function QuestionCard({
  question,
  options,
  selectedAnswer,
  correctAnswer,
  showResult,
  onAnswer,
  quizTopic,
  questionKey,
  onReport,
}: {
  question: string;
  options: string[];
  selectedAnswer: number | null;
  correctAnswer: number | null;
  showResult: boolean;
  onAnswer: (index: number) => void;
  quizTopic?: string;
  questionKey: string;
  onReport?: () => void;
}) {
  return (
    <div key={questionKey} className="flex-1 flex flex-col p-3 animate-fadeInUp">
      {/* Question */}
      <div className="bg-white rounded-2xl p-4 mb-2 shadow-md shadow-purple-500/10">
        {quizTopic && (
          <div className="inline-flex items-center gap-1 bg-[#E5F2EF] text-[#0F766E] rounded-full px-2 py-0.5 text-xs font-medium mb-2">
            <span>📚</span>
            <span>{quizTopic}</span>
          </div>
        )}
        <div className="flex justify-between items-start gap-2">
          <h2 className="flex-1 text-base font-bold text-gray-900 leading-snug">
            {question}
          </h2>
          <button
            onClick={onReport}
            className="shrink-0 w-7 h-7 flex items-center justify-center text-[#626B73] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Сообщить об ошибке"
          >
            🚩
          </button>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-2">
        {options.map((option, index) => {
          const isSelected = selectedAnswer === index;
          const isCorrect = correctAnswer === index;
          const isWrong = showResult && isSelected && !isCorrect;
          
          let cardStyle = 'bg-white border-2 border-gray-100 hover:border-purple-300';
          let iconBg = 'bg-gray-100 text-gray-600';
          let animation = '';
          
          if (showResult) {
            if (isCorrect) {
              cardStyle = 'bg-green-50 border-2 border-green-400';
              iconBg = 'bg-green-500 text-white';
              animation = 'animate-bounce';
            } else if (isWrong) {
              cardStyle = 'bg-red-50 border-2 border-red-400';
              iconBg = 'bg-red-500 text-white';
              animation = 'animate-shake';
            } else {
              cardStyle = 'bg-gray-50 border-2 border-gray-100 opacity-50';
            }
          } else if (isSelected) {
            cardStyle = 'bg-purple-50 border-2 border-[#0F766E]';
            iconBg = 'bg-[#0F766E] text-white';
          }

          return (
            <button
              key={index}
              onClick={() => !showResult && onAnswer(index)}
              disabled={showResult}
              className={`w-full p-3 rounded-xl text-left transition-all duration-200 ${cardStyle} ${animation}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold transition-colors ${iconBg}`}>
                  {showResult && isCorrect ? '✓' : showResult && isWrong ? '✗' : String.fromCharCode(65 + index)}
                </div>
                <span className="flex-1 text-sm text-gray-800">{option}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ==================== Hotspot Card: клик по фото ====================
/** Зоны хранятся в % от картинки; рисуем поверх <img> абсолютным слоем и SVG с viewBox 0 0 100 100. */
type HotspotPoint = { x: number; y: number };
interface HotspotAllResult {
  points: HotspotPoint[];
  zones: HotspotZone[];
  hits: Record<string, boolean>;
  missed: string[];
  stray: number;
}

// Геометрия нужна только для цвета отметок после ответа сервера.
function hotspotContains(pt: HotspotPoint, z: HotspotZone): boolean {
  const c = z.coords || [];
  if (z.shape === 'rect' && c.length >= 4) {
    return pt.x >= c[0] && pt.x <= c[0] + c[2] && pt.y >= c[1] && pt.y <= c[1] + c[3];
  }
  let inside = false;
  if (z.shape === 'poly' && c.length >= 6) {
    for (let i = 0, j = c.length - 2; i < c.length; j = i, i += 2) {
      if ((c[i + 1] > pt.y) !== (c[j + 1] > pt.y)
        && pt.x < (c[j] - c[i]) * (pt.y - c[i + 1]) / (c[j + 1] - c[i + 1]) + c[i]) inside = !inside;
    }
  }
  return inside;
}

function HotspotCard({
  question, image, result, showResult, isCorrect, onTap, quizTopic, questionKey, onReport,
  mode, allResult, busy, error, onCheck,
}: {
  question: string;
  image?: { url: string; alt?: string };
  mode?: 'one' | 'all';
  allResult: HotspotAllResult | null;
  busy: boolean;
  error: string | null;
  onCheck: (points: HotspotPoint[]) => void;
  result: { point: { x: number; y: number }; hit: string | null; zones: any[] } | null;
  showResult: boolean;
  isCorrect: boolean;
  onTap: (point: { x: number; y: number }) => void;
  quizTopic?: string;
  questionKey: string;
  onReport?: () => void;
}) {
  const [points, setPoints] = useState<HotspotPoint[]>([]);
  const all = mode === 'all';
  const locked = showResult || busy;
  const tap = (e: React.MouseEvent<HTMLDivElement>) => {
    if (locked) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 1000) / 10;
    const y = Math.round(((e.clientY - r.top) / r.height) * 1000) / 10;
    const point = { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
    if (all) setPoints(previous => previous.length < 12 ? [...previous, point] : previous);
    else onTap(point);
  };
  const shown: HotspotZone[] = all ? (allResult?.zones || []).filter(z => z.correct || allResult?.hits[z.id]) : showResult && result ? result.zones : [];
  const pt = result?.point;
  return (
    <div key={questionKey} className="flex-1 flex flex-col p-3 animate-fadeInUp">
      <div className="bg-white rounded-2xl p-4 mb-2 shadow-md shadow-purple-500/10">
        {quizTopic && (
          <div className="inline-flex items-center gap-1 bg-[#E5F2EF] text-[#0F766E] rounded-full px-2 py-0.5 text-xs font-medium mb-2">
            <span>📚</span><span>{quizTopic}</span>
          </div>
        )}
        <div className="flex justify-between items-start gap-2">
          <h2 className="flex-1 text-base font-bold text-gray-900 leading-snug">{question}</h2>
          <button onClick={onReport} className="shrink-0 w-7 h-7 flex items-center justify-center text-[#626B73] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Сообщить об ошибке">🚩</button>
        </div>
        {!showResult && <p className="mt-1 text-sm text-[#626B73]">{all ? 'Отметьте все верные зоны. Нажмите на маркер, чтобы снять его. Максимум 12 отметок.' : 'Нажмите на нужное место на фото'}</p>}
      </div>
      {image?.url ? (
        <div
          className={`relative w-full overflow-hidden rounded-2xl border-2 bg-white ${showResult ? (isCorrect ? 'border-green-400' : 'border-red-400') : 'border-gray-100 cursor-crosshair'}`}
          onClick={tap}
        >
          <img src={image.url} alt={image.alt || ''} className="block w-full select-none" draggable={false} />
          {shown.length > 0 && (
            <svg className="absolute inset-0 h-full w-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
              {shown.map((z: any) => {
                const missed = all && allResult?.missed.includes(z.id);
                const stroke = missed ? '#ca8a04' : z.correct ? '#16a34a' : '#dc2626';
                const fill = missed ? 'rgba(234,179,8,0.18)' : z.correct ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.06)';
                if (z.shape === 'rect' && z.coords?.length >= 4) {
                  return <rect key={z.id} x={z.coords[0]} y={z.coords[1]} width={z.coords[2]} height={z.coords[3]} fill={fill} stroke={stroke} strokeWidth={0.45} vectorEffect="non-scaling-stroke" />;
                }
                if (z.shape === 'poly' && z.coords?.length >= 6) {
                  const pts: string[] = [];
                  for (let i = 0; i < z.coords.length; i += 2) pts.push(`${z.coords[i]},${z.coords[i + 1]}`);
                  return <polygon key={z.id} points={pts.join(' ')} fill={fill} stroke={stroke} strokeWidth={0.45} vectorEffect="non-scaling-stroke" />;
                }
                return null;
              })}
            </svg>
          )}
          {all && allResult && shown.filter(z => allResult.missed.includes(z.id)).map(z => (
            <span key={z.id} className="pointer-events-none absolute rounded bg-yellow-100 px-1 text-xs text-yellow-800"
              style={{ left: `${z.coords[0]}%`, top: `${z.coords[1]}%` }}>пропущено</span>
          ))}
          {all && (allResult?.points || points).map((point, index) => {
            const hitZones = allResult?.zones.filter(z => hotspotContains(point, z));
            const tone = !hitZones ? 'bg-[#0F766E]' : hitZones.some(z => z.correct === false)
              ? 'bg-red-500' : hitZones.length ? 'bg-green-500' : 'bg-gray-500';
            return <button key={index} type="button" disabled={locked}
              aria-label={`Снять отметку ${index + 1}`}
              onClick={e => { e.stopPropagation(); if (!locked) setPoints(previous => previous.filter((_, i) => i !== index)); }}
              className={`absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white text-xs text-white shadow ${tone}`}
              style={{ left: `${point.x}%`, top: `${point.y}%` }}>{index + 1}</button>;
          })}
          {pt && (
            <div
              className={`absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}
              style={{ left: `${pt.x}%`, top: `${pt.y}%` }}
            />
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">У вопроса нет картинки — сообщите об ошибке.</div>
      )}
      {all && <p className="mt-2 text-sm text-[#626B73]" aria-live="polite">Отмечено {(allResult?.points || points).length}</p>}
      {all && !showResult && <button type="button" disabled={locked || points.length === 0 || !image?.url}
        onClick={() => onCheck([...points])}
        className="mt-3 min-h-[44px] rounded-xl bg-[#0F766E] px-4 py-3 font-semibold text-white disabled:opacity-40">
        {busy ? 'Проверяем…' : 'Проверить'}
      </button>}
      {all && error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      {all && allResult && <div className="mt-3 space-y-2" aria-live="polite">
        {allResult.zones.filter(z => allResult.missed.includes(z.id) || (z.correct === false && allResult.hits[z.id])).map(z => (
          <p key={z.id} className={`rounded-xl px-3 py-2 text-sm ${z.correct ? 'bg-yellow-50 text-yellow-800' : 'bg-red-50 text-red-700'}`}>
            {z.correct ? 'Пропущено' : 'Ловушка'}: {z.label || z.id}{z.explanation ? ` — ${z.explanation}` : ''}
          </p>
        ))}
        {allResult.stray > 0 && <p className="text-sm text-gray-600">Мимо всех зон: {allResult.stray}</p>}
      </div>}
      {!all && showResult && shown.length > 0 && (
        <p className="mt-2 text-xs text-[#626B73]">Зелёным — верная зона{shown.some((z: any) => !z.correct) ? ', красным — ловушки' : ''}.</p>
      )}
    </div>
  );
}

interface LabelsQuestion {
  text: string;
  image?: { url: string; alt?: string };
  zones?: HotspotZone[];
  labels?: string[];
  quizTopic?: string;
}

interface LabelsResult {
  placements: Record<string, string>;
  perZone: Record<string, boolean>;
  zones: HotspotZone[];
}

function LabelsCard({ question, result, busy, error, onCheck, onReport }: {
  question: LabelsQuestion;
  result: LabelsResult | null;
  busy: boolean;
  error: string | null;
  onCheck: (placements: Record<string, string>) => void;
  onReport: () => void;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  // Индексы позволяют использовать одинаковые подписи несколько раз.
  const [assigned, setAssigned] = useState<Record<string, number>>({});
  const zones = question.zones || [];
  const labels = question.labels || [];
  const locked = busy || !!result;
  const assignedIndex = (id: string): number | undefined => Object.prototype.hasOwnProperty.call(assigned, id) ? assigned[id] : undefined;
  const filled = zones.length > 0 && zones.every(z => assignedIndex(z.id) !== undefined);
  const tap = (id: string) => {
    if (locked) return;
    if (assignedIndex(id) !== undefined) {
      setAssigned(prev => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== id)));
    } else if (selected !== null) {
      setAssigned(prev => ({ ...prev, [id]: selected }));
      setSelected(null);
    }
  };
  const check = () => {
    if (!filled || locked) return;
    onCheck(Object.fromEntries(zones.map(z => [z.id, labels[assigned[z.id]]])));
  };
  return (
    <div className="flex-1 flex flex-col p-3 animate-fadeInUp">
      <div className="bg-white rounded-2xl p-4 mb-2 shadow-md shadow-purple-500/10">
        {question.quizTopic && <div className="inline-flex bg-[#E5F2EF] text-[#0F766E] rounded-full px-2 py-0.5 text-xs font-medium mb-2">📚 {question.quizTopic}</div>}
        <div className="flex justify-between items-start gap-2">
          <h2 className="flex-1 text-base font-bold text-gray-900 leading-snug">{question.text}</h2>
          <button type="button" onClick={onReport} className="shrink-0 w-7 h-7 text-[#626B73] rounded-lg" title="Сообщить об ошибке">🚩</button>
        </div>
        {!result && <p className="mt-1 text-sm text-[#626B73]">Выберите подпись, затем точку на фото. Нажмите заполненную точку, чтобы снять подпись.</p>}
      </div>
      {question.image?.url ? (
        <div className="relative w-full rounded-2xl border-2 border-gray-100 bg-white">
          <img src={question.image.url} alt={question.image.alt || ''} className="block w-full rounded-2xl select-none" draggable={false} />
          {result && <svg className="absolute inset-0 h-full w-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            {zones.map(z => {
              const color = result.perZone[z.id] ? '#16a34a' : '#dc2626';
              const style = { fill: color, fillOpacity: 0.18, stroke: color, strokeWidth: 2 };
              return z.shape === 'rect'
                ? <rect key={z.id} x={z.coords[0]} y={z.coords[1]} width={z.coords[2]} height={z.coords[3]} {...style} vectorEffect="non-scaling-stroke" />
                : <polygon key={z.id} points={Array.from({ length: z.coords.length / 2 }, (_, i) => z.coords.slice(i * 2, i * 2 + 2).join(',')).join(' ')} {...style} vectorEffect="non-scaling-stroke" />;
            })}
          </svg>}
          {zones.map((z, i) => {
            const pairs = z.coords.length / 2;
            // Кружок ставим в marker (если задан в данных: [x, y] в %), иначе в центр зоны — чтобы не закрывать характерную часть детали
            const mk = (z as any).marker as number[] | undefined;
            const x = mk && mk.length >= 2 ? mk[0] : z.shape === 'rect' ? z.coords[0] + z.coords[2] / 2
              : z.coords.filter((_, k) => k % 2 === 0).reduce((sum, n) => sum + n, 0) / pairs;
            const y = mk && mk.length >= 2 ? mk[1] : z.shape === 'rect' ? z.coords[1] + z.coords[3] / 2
              : z.coords.filter((_, k) => k % 2 === 1).reduce((sum, n) => sum + n, 0) / pairs;
            const index = assignedIndex(z.id);
            const label = result ? result.placements[z.id] : index === undefined ? undefined : labels[index];
            // На схеме только маленький кружок с номером: подписи живут внизу, рисунок не перекрывается
            const tone = result
              ? (result.perZone[z.id] ? 'bg-green-600 border-white' : 'bg-red-600 border-white')
              : label ? 'bg-[#0F766E] border-white' : selected !== null ? 'bg-white border-[#0F766E] text-[#0F766E] animate-pulse' : 'bg-white border-[#0F766E] text-[#0F766E]';
            return <button key={z.id} type="button" disabled={locked}
              onClick={() => tap(z.id)}
              aria-label={`Зона ${i + 1}${label ? ': ' + label + '. Снять подпись' : '. Поставить подпись'}`}
              className={`absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 text-xs font-bold shadow ${label || result ? 'text-white' : ''} ${tone}`}
              style={{ left: `${x}%`, top: `${y}%` }}>
              {i + 1}
            </button>;
          })}
        </div>
      ) : <p className="rounded-2xl bg-red-50 p-4 text-sm text-red-700">У вопроса нет картинки — сообщите об ошибке.</p>}
      {!result && <div className="mt-3 flex flex-wrap gap-2">
        {labels.map((label, i) => {
          const zoneEntry = Object.entries(assigned).find(([, idx]) => idx === i);
          const zoneNo = zoneEntry ? zones.findIndex(z => z.id === zoneEntry[0]) + 1 : 0;
          const used = zoneNo > 0;
          return <button key={i} type="button" disabled={locked} aria-pressed={selected === i}
            onClick={() => used ? tap(zoneEntry![0]) : setSelected(selected === i ? null : i)}
            title={used ? 'Снять с точки' : 'Выбрать подпись'}
            className={`min-h-[44px] rounded-xl border-2 px-3 py-2 text-sm ${selected === i ? 'border-[#0F766E] bg-[#0F766E] text-white' : used ? 'border-[#0F766E] bg-white text-[#252A2D]' : 'border-[#E5F2EF] bg-[#E5F2EF] text-[#0F766E]'}`}>
            {used && <span className="mr-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#0F766E] text-xs font-bold text-white">{zoneNo}</span>}{label}
          </button>;
        })}
      </div>}
      {result && <div className="mt-3 space-y-1.5" aria-live="polite">
        {zones.map((z, i) => {
          const ok = !!result.perZone[z.id];
          const correctZone = result.zones.find(zone => zone.id === z.id);
          return <div key={z.id} className={`rounded-xl px-3 py-2 text-sm ${ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            <p><span className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white ${ok ? 'bg-green-600' : 'bg-red-600'}`}>{i + 1}</span>
              {result.placements[z.id]} {ok ? '✓' : `✕ — верно: ${correctZone?.label ?? ''}`}</p>
            {!ok && correctZone?.explanation && <p className="mt-1 text-xs">{correctZone.explanation}</p>}
          </div>;
        })}
      </div>}
      {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      {!result && <button type="button" onClick={check} disabled={!filled || locked || !question.image?.url}
        className="mt-3 min-h-[44px] rounded-xl bg-[#0F766E] px-4 py-3 font-semibold text-white disabled:opacity-40">
        {busy ? 'Проверяем…' : 'Проверить'}
      </button>}
    </div>
  );
}

interface LineQuestion {
  text: string;
  quizTopic?: string;
  image?: { url: string; alt?: string };
  lines?: Omit<LineOption, 'correct'>[];
}
interface LineResult { lines: LineOption[]; chosen: string }

function LineCard({ question, result, busy, error, onCheck, onReport }: {
  question: LineQuestion;
  result: LineResult | null;
  busy: boolean;
  error: string | null;
  onCheck: (lineId: string) => void;
  onReport: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const locked = busy || !!result;
  const lines = result?.lines || question.lines || [];
  const chosen = result?.chosen ?? selected;
  const color = (id: string) => result
    ? result.lines.find(line => line.id === id)?.correct ? '#16a34a' : id === chosen ? '#dc2626' : '#9ca3af'
    : '#0F766E';
  const choose = (id: string) => { if (!locked) setSelected(id); };
  return <div className="flex-1 flex flex-col gap-3 p-3 animate-fadeInUp">
    <div className="rounded-2xl bg-white p-4">
      {question.quizTopic && <p className="text-xs text-[#0F766E]">📚 {question.quizTopic}</p>}
      <div className="flex items-start gap-2">
        <h2 className="flex-1 text-base font-bold">{question.text}</h2>
        <button type="button" onClick={onReport} title="Сообщить об ошибке">🚩</button>
      </div>
    </div>
    {question.image?.url ? <div className="relative overflow-hidden rounded-2xl">
      <img src={question.image.url} alt={question.image.alt || 'Выберите линию на фото'} className="block h-auto w-full" draggable={false} />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
        {lines.map(line => {
          const points = Array.from({ length: line.points.length / 2 }, (_, i) => `${line.points[2 * i]},${line.points[2 * i + 1]}`).join(' ');
          return <g key={line.id} onClick={() => choose(line.id)} className={locked ? '' : 'cursor-pointer'}>
            {chosen === line.id && <polyline points={points} fill="none" stroke="white" strokeOpacity={0.8} strokeWidth={7} vectorEffect="non-scaling-stroke" />}
            <polyline points={points} fill="none" stroke={color(line.id)} strokeWidth={chosen === line.id ? 3 : 1.2} vectorEffect="non-scaling-stroke" />
            <polyline points={points} fill="none" stroke="transparent" strokeWidth={18} vectorEffect="non-scaling-stroke" pointerEvents="stroke" />
          </g>;
        })}
      </svg>
      {lines.map((line, i) => <button key={line.id} type="button" disabled={locked} onClick={() => choose(line.id)}
        aria-label={`Выбрать линию ${String.fromCharCode(65 + i)}`} aria-pressed={chosen === line.id}
        style={{ left: `${line.points[0]}%`, top: `${line.points[1]}%`, backgroundColor: color(line.id) }}
        className={`absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white text-xs font-bold text-white shadow ${chosen === line.id ? 'ring-4 ring-white/70' : ''}`}>
        {String.fromCharCode(65 + i)}
      </button>)}
    </div> : <p role="alert">У вопроса нет картинки — сообщите об ошибке.</p>}
    <div className="space-y-2">
      {lines.map((line, i) => <button key={line.id} type="button" disabled={locked} aria-pressed={chosen === line.id}
        onClick={() => choose(line.id)} style={{ borderColor: color(line.id), color: color(line.id) }}
        className={`block min-h-[44px] w-full rounded-xl border-2 px-3 py-2 text-left ${chosen === line.id ? 'bg-[#E5F2EF] font-bold' : 'bg-white'}`}>
        {String.fromCharCode(65 + i)} — {line.label || 'Линия'}
        {result && (result.lines.find(item => item.id === line.id)?.correct ? ' ✓' : chosen === line.id ? ' ✕' : '')}
      </button>)}
    </div>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {!result && <button type="button" disabled={locked || selected === null || !question.image?.url}
      onClick={() => { if (selected !== null) onCheck(selected); }}
      className="min-h-[44px] rounded-xl bg-[#0F766E] px-4 py-3 font-semibold text-white disabled:opacity-40">
      {busy ? 'Проверяем…' : 'Проверить'}
    </button>}
  </div>;
}

interface OrderQuestion {
  text: string;
  quizTopic?: string;
  steps?: { id: string; text: string }[];
}

interface CategoriesQuestion {
  text: string;
  quizTopic?: string;
  categories?: { id: string; name: string }[];
  items?: { id: string; text: string }[];
}
interface CategoriesResult {
  assignment: Record<string, string>;
  perItem: Record<string, boolean>;
  items: { id: string; text: string; category: string }[];
}

function CategoriesCard({ question, result, busy, error, onCheck, onReport }: {
  question: CategoriesQuestion;
  result: CategoriesResult | null;
  busy: boolean;
  error: string | null;
  onCheck: (assignment: Record<string, string>) => void;
  onReport: () => void;
}) {
  const [assignment, setAssignment] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const categories = question.categories || [];
  const items = question.items || [];
  const locked = busy || !!result;
  const placed = result?.assignment || assignment;
  const categoryOf = (id: string) => Object.prototype.hasOwnProperty.call(placed, id) ? placed[id] : undefined;
  const categoryName = (id?: string) => categories.find(c => c.id === id)?.name || id;
  const tapItem = (id: string) => {
    if (locked) return;
    if (categoryOf(id) !== undefined) {
      setAssignment(previous => {
        const next = { ...previous };
        delete next[id];
        return next;
      });
      setSelected(null);
    } else setSelected(previous => previous === id ? null : id);
  };
  const renderItem = (item: { id: string; text: string }) => {
    const ok = result?.perItem[item.id];
    return <li key={item.id}>
      <button type="button" disabled={locked} onClick={() => tapItem(item.id)} aria-pressed={selected === item.id}
        className={`w-full min-h-[44px] rounded-xl border-2 p-3 text-left text-sm break-words ${result
          ? ok ? 'border-green-400 bg-green-50 text-green-800' : 'border-red-400 bg-red-50 text-red-800'
          : selected === item.id ? 'border-[#0F766E] bg-[#E5F2EF]' : 'border-gray-200 bg-white'}`}>
        <span>{item.text}{result ? ok ? ' ✓' : ' ✕' : ''}</span>
        {categoryOf(item.id) !== undefined && <span className="block text-xs mt-1">{categoryName(categoryOf(item.id))}</span>}
        {result && !ok && <span className="block mt-1 font-semibold">Верно: {categoryName(result.items.find(i => i.id === item.id)?.category)}</span>}
      </button>
    </li>;
  };
  return <div className="flex-1 flex flex-col p-3 animate-fadeInUp">
    <div className="bg-white rounded-2xl p-4 mb-2 shadow-md">
      {question.quizTopic && <p className="text-xs text-[#0F766E] mb-2">📚 {question.quizTopic}</p>}
      <div className="flex items-start gap-2">
        <h2 className="flex-1 text-base font-bold text-gray-900">{question.text}</h2>
        <button type="button" onClick={onReport} title="Сообщить об ошибке" className="w-7 h-7 shrink-0">🚩</button>
      </div>
      {!result && <p className="mt-2 text-sm text-[#626B73]">Нажмите на предмет, затем на категорию. Нажмите на разложенный предмет, чтобы вернуть его.</p>}
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
      {categories.map(category => <section key={category.id} className="min-w-0">
        <button type="button" disabled={locked || selected === null} onClick={() => {
          if (locked || selected === null) return;
          setAssignment(previous => ({ ...previous, [selected]: category.id }));
          setSelected(null);
        }} className="min-h-[44px] w-full rounded-xl bg-[#0F766E] p-2 text-sm font-semibold text-white break-words disabled:opacity-60">
          {category.name} ({items.filter(item => categoryOf(item.id) === category.id).length})
        </button>
        <ul className="mt-2 space-y-2">{items.filter(item => categoryOf(item.id) === category.id).map(renderItem)}</ul>
      </section>)}
    </div>
    <p className="text-sm text-[#626B73] mb-2" aria-live="polite">Разложено {items.filter(item => categoryOf(item.id) !== undefined).length} из {items.length}</p>
    <ul className="space-y-2">{items.filter(item => categoryOf(item.id) === undefined).map(renderItem)}</ul>
    {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
    {!result && <button type="button" disabled={locked || items.length < 4 || !items.every(item => categoryOf(item.id) !== undefined)}
      onClick={() => onCheck({ ...assignment })} className="mt-3 min-h-[44px] rounded-xl bg-[#0F766E] p-3 font-semibold text-white disabled:opacity-40">
      {busy ? 'Проверяем…' : 'Проверить'}
    </button>}
  </div>;
}

interface OrderResult {
  order: string[];
  correctOrder: string[];
  perStep: Record<string, boolean>;
  steps: { id: string; text: string }[];
}

function OrderCard({ question, result, busy, error, onCheck, onReport }: {
  question: OrderQuestion;
  result: OrderResult | null;
  busy: boolean;
  error: string | null;
  onCheck: (order: string[]) => void;
  onReport: () => void;
}) {
  const [order, setOrder] = useState(() => (question.steps || []).map(step => step.id));
  const locked = busy || !!result;
  const steps = question.steps || [];
  const move = (index: number, offset: number) => {
    if (locked || index + offset < 0 || index + offset >= order.length) return;
    setOrder(previous => {
      const next = [...previous];
      [next[index], next[index + offset]] = [next[index + offset], next[index]];
      return next;
    });
  };
  return (
    <div className="flex-1 flex flex-col p-3 animate-fadeInUp">
      <div className="bg-white rounded-2xl p-4 mb-2 shadow-md shadow-purple-500/10">
        {question.quizTopic && <div className="inline-flex bg-[#E5F2EF] text-[#0F766E] rounded-full px-2 py-0.5 text-xs font-medium mb-2">📚 {question.quizTopic}</div>}
        <div className="flex justify-between items-start gap-2">
          <h2 className="flex-1 text-base font-bold text-gray-900 leading-snug">{question.text}</h2>
          <button type="button" onClick={onReport} className="shrink-0 w-7 h-7 text-[#626B73] rounded-lg" title="Сообщить об ошибке">🚩</button>
        </div>
        {!result && <p className="mt-1 text-sm text-[#626B73]">Расставьте шаги по порядку кнопками ▲ и ▼.</p>}
      </div>
      <ol className="space-y-2">
        {(result?.order || order).map((id, index) => {
          const ok = !!result?.perStep[id];
          return <li key={id} className={`flex items-center gap-2 rounded-xl p-2 text-sm ${result ? ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700' : 'bg-[#E5F2EF] text-[#0F766E]'}`}>
            <span className="w-6 shrink-0 text-center font-bold">{index + 1}.</span>
            <span className="min-w-0 flex-1 break-words">{steps.find(step => step.id === id)?.text}</span>
            {result ? <span aria-label={ok ? 'На своём месте' : 'Не на своём месте'}>{ok ? '✓' : '✕'}</span> : <div className="flex shrink-0">
              <button type="button" onClick={() => move(index, -1)} disabled={locked || index === 0}
                aria-label={`Шаг ${index + 1}: выше`} className="min-h-[44px] min-w-[44px] rounded-lg disabled:opacity-30">▲</button>
              <button type="button" onClick={() => move(index, 1)} disabled={locked || index === order.length - 1}
                aria-label={`Шаг ${index + 1}: ниже`} className="min-h-[44px] min-w-[44px] rounded-lg disabled:opacity-30">▼</button>
            </div>}
          </li>;
        })}
      </ol>
      {result && <div className="mt-3 rounded-xl bg-white p-3 text-sm text-[#626B73]" aria-live="polite">
        <p className="font-semibold text-[#0F766E]">Правильный порядок:</p>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          {result.correctOrder.map(id => <li key={id}>{result.steps.find(step => step.id === id)?.text}</li>)}
        </ol>
      </div>}
      {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      {!result && <button type="button" onClick={() => onCheck([...order])} disabled={locked || order.length < 3}
        className="mt-3 min-h-[44px] rounded-xl bg-[#0F766E] px-4 py-3 font-semibold text-white disabled:opacity-40">
        {busy ? 'Проверяем…' : 'Проверить'}
      </button>}
    </div>
  );
}

// ==================== Explanation Card ====================
interface TextResult { value: string; correct: boolean; correctAnswer?: string }
interface TextCardProps {
  question: { text: string; unit?: string; maxLength?: number };
  result: TextResult | null;
  busy: boolean;
  error: string | null;
  onCheck: (value: string) => void;
  onReport: () => void;
}

function TextAnswerCard({ question, result, busy, error, onCheck, onReport, numeric }: TextCardProps & { numeric: boolean }) {
  const [value, setValue] = useState('');
  const ready = numeric ? parseNumber(value) !== null : !!value.trim();
  return <div className="mx-4 rounded-2xl bg-white p-5 shadow-sm">
    <h2 className="mb-5 text-xl font-semibold text-gray-900">{question.text}</h2>
    <form onSubmit={e => { e.preventDefault(); if (ready && !busy && !result) onCheck(value); }}>
      <div className="flex items-center gap-3">
        <input aria-label="Ваш ответ" inputMode={numeric ? 'decimal' : 'text'}
          maxLength={numeric ? 300 : question.maxLength ?? 200} value={result?.value ?? value}
          disabled={busy || !!result} onChange={e => setValue(e.target.value)}
          className={`min-w-0 flex-1 rounded-xl border-2 p-3 text-gray-900 ${result ? result.correct ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50' : 'border-gray-300'}`} />
        {numeric && question.unit && <span className="text-gray-600">{question.unit}</span>}
      </div>
      {result && !result.correct && <p role="status" className="mt-3 text-green-700">Верно: {result.correctAnswer}</p>}
      {error && <p role="alert" className="mt-3 text-red-600">{error}</p>}
      {!result && <button type="submit" disabled={!ready || busy}
        className="mt-4 min-h-[44px] w-full rounded-xl bg-[#0F766E] p-3 font-semibold text-white disabled:opacity-40">
        {busy ? 'Проверяем…' : 'Проверить'}
      </button>}
    </form>
    <button type="button" onClick={onReport} className="mt-3 text-xs text-gray-500">Сообщить об ошибке</button>
  </div>;
}

function NumberCard(props: TextCardProps) { return <TextAnswerCard {...props} numeric />; }
function FreeCard(props: TextCardProps) { return <TextAnswerCard {...props} numeric={false} />; }

function ExplanationCard({ 
  explanation, 
  isCorrect,
  onNext 
}: { 
  explanation: string;
  isCorrect: boolean;
  onNext: () => void;
}) {
  return (
    <div className="mx-3 mb-3 animate-fadeInUp">
      <div className={`p-3 rounded-xl border ${
        isCorrect 
          ? 'bg-green-50 border-green-200' 
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-start gap-2 mb-3">
          <span className="text-lg">{isCorrect ? '🎯' : '💡'}</span>
          <p className={`text-sm ${isCorrect ? 'text-green-700' : 'text-blue-700'}`}>
            {explanation}
          </p>
        </div>
        
        <button
          onClick={onNext}
          className={`w-full py-2.5 rounded-lg font-semibold text-[#252A2D] transition-all active:scale-[0.98] ${
            isCorrect 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-[#0F766E] hover:bg-[#0B5F59]'
          }`}
        >
          Далее →
        </button>
      </div>
    </div>
  );
}

// ==================== Result Screen ====================
function ResultScreen({
  score,
  passed,
  xpEarned,
  newBestScore,
  moduleCompleted,
  collectionCompleted,
  correctCount,
  totalCount,
  maxStreak,
  collectionId,
  certificate,
  onContinue,
  onRetry,
}: {
  score: number;
  passed: boolean;
  xpEarned: number;
  newBestScore: boolean;
  moduleCompleted: boolean;
  collectionCompleted: boolean;
  correctCount: number;
  totalCount: number;
  maxStreak: number;
  collectionId?: string;
  certificate?: { visibleId: string; score: number; collectionName: string } | null;
  onContinue: () => void;
  onRetry: () => void;
}) {
  const certUrl = certificate?.visibleId ? `/api/user/certificates/${certificate.visibleId}/image` : null;
  const shareCertificate = () => {
    if (!certificate) return;
    const platform = detectPlatform();
    const stars = certificate.score >= 90 ? '⭐⭐⭐' : certificate.score >= 70 ? '⭐⭐' : '⭐';
    const url = platform === 'telegram' ? coursesBotUrl(collectionId ? `collection_${collectionId}` : undefined)
      : window.location.origin + (collectionId ? `/course/${collectionId}` : '/collections');
    shareResult({ text: `🎓 Я прошёл курс «${certificate.collectionName}» — ${certificate.score}% ${stars}\nСертификат ${certificate.visibleId}`, url });
  };
  const handleShare = () => {
    const platform = detectPlatform();
    const emoji = collectionCompleted ? '🏆' : passed ? '🎉' : '📚';
    const status = collectionCompleted
      ? 'завершил курс!'
      : passed
        ? `прошёл тест на ${score}%!`
        : `набрал ${score}%`;
    
    let url: string;
    if (platform === 'vk') {
      url = collectionId ? `https://vk.com/app52236695#collection=${collectionId}` : 'https://vk.com/app52236695';
    } else if (platform === 'telegram') {
      url = coursesBotUrl(collectionId ? `collection_${collectionId}` : undefined);
    } else {
      url = collectionId ? `https://beshek.mugstel.ru/collections/${collectionId}` : 'https://beshek.mugstel.ru/collections';
    }
    
    const text = `${emoji} Я ${status}\n\nПопробуй тоже!`;
    shareResult({ text, url });
  };

  return (
    <div className="min-h-screen bg-[#F7F7F4] flex flex-col items-center justify-center p-4">
      <style>{animationStyles}</style>
      
      <div className="w-full max-w-sm bg-white rounded-2xl p-5 shadow-2xl animate-scaleIn">
        <div className="text-center mb-4">
          <div className="text-6xl mb-2 animate-bounce">
            {collectionCompleted ? '🏆' : passed ? '🎉' : '💪'}
          </div>
          
          <div className={`text-5xl font-black mb-1 ${
            passed ? 'text-green-500' : 'text-orange-500'
          }`}>
            {score}%
          </div>
          
          <p className="text-[#626B73] text-sm">
            {correctCount} из {totalCount} правильных
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-yellow-50 rounded-xl p-3 text-center">
            <div className="text-xl">⭐</div>
            <div className="text-xl font-bold text-yellow-600">+{xpEarned}</div>
            <div className="text-xs text-yellow-700">XP</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-3 text-center">
            <div className="text-xl">🔥</div>
            <div className="text-xl font-bold text-orange-600">{maxStreak}</div>
            <div className="text-xs text-orange-700">макс. серия</div>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {collectionCompleted && (
            <div className="bg-amber-100 rounded-lg p-2 text-center">
              <span className="font-semibold text-amber-700 text-sm">🏆 Курс завершён!</span>
            </div>
          )}
          {collectionCompleted && certUrl && (
            <div className="rounded-lg overflow-hidden border border-amber-200">
              <img src={certUrl} alt={`Сертификат ${certificate?.visibleId}`} className="w-full block" />
              <div className="p-2 space-y-2 bg-amber-50">
                <button onClick={shareCertificate} className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg text-sm">📤 Поделиться сертификатом</button>
                <a href="/profile" className="block text-center text-xs text-amber-800 underline">Все дипломы — в профиле</a>
              </div>
            </div>
          )}
          {moduleCompleted && !collectionCompleted && (
            <div className="bg-green-100 rounded-lg p-2 text-center">
              <span className="font-semibold text-green-700 text-sm">✅ Модуль пройден!</span>
            </div>
          )}
          {!passed && (
            <div className="bg-gray-100 rounded-lg p-2 text-center">
              <span className="text-gray-600 text-sm">Нужно минимум 70%</span>
            </div>
          )}
          {newBestScore && passed && (
            <div className="bg-blue-100 rounded-lg p-2 text-center">
              <span className="font-semibold text-blue-700 text-sm">⭐ Новый рекорд!</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          {passed ? (
            <button
              onClick={onContinue}
              className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all active:scale-[0.98]"
            >
              {collectionCompleted ? '🏠 К коллекциям' : '➡️ Продолжить'}
            </button>
          ) : (
            <button
              onClick={onRetry}
              className="w-full py-3 bg-[#0F766E] hover:bg-[#0B5F59] text-white font-bold rounded-xl transition-all active:scale-[0.98]"
            >
              🔄 Попробовать снова
            </button>
          )}

          {/* Момент, когда человек доволен собой, — единственный удачный, чтобы позвать */}
          {/* «Напоминать мне» убрана 07.09.2026: на сайте вела в Telegram (правило дверей), а рассылки напоминаний нет */}

          <button
            onClick={handleShare}
            className="w-full py-3 bg-[#0F766E] hover:bg-[#0B5F59] text-white font-semibold rounded-xl transition-all active:scale-[0.98]"
          >
            📤 Поделиться
          </button>

          <button
            onClick={onContinue}
            className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm rounded-xl transition-colors"
          >
            К модулям
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== Loading Screen ====================
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center">
      <style>{animationStyles}</style>
      <div className="text-center">
        <div className="w-14 h-14 border-4 border-[#E1E5E3] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#626B73]">Загрузка...</p>
      </div>
    </div>
  );
}

// ==================== Main Page ====================

/** Цели Яндекс.Метрики (счётчик задан в index.html как _mugCounter). Вместо своего журнала событий — решение владельца 07.09.2026. */
function metrikaGoal(goal: string, params: Record<string, unknown>) {
  try {
    const w = window as any;
    if (typeof w.ym === 'function' && w._mugCounter) w.ym(w._mugCounter, 'reachGoal', goal, params);
  } catch { /* метрика не загрузилась — не мешаем тесту */ }
}

export default function CollectionPlayPage() {
  const { id, moduleId } = useParams<{ id: string; moduleId: string }>();
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const resultSentRef = useRef(false);

  const session = useCollectionsStore((state) => state.session);
  const result = useCollectionsStore((state) => state.result);
  const isLoading = useCollectionsStore((state) => state.isLoading);
  const error = useCollectionsStore((state) => state.error);

  const startModule = useCollectionsStore((state) => state.startModule);
  const answerQuestion = useCollectionsStore((state) => state.answerQuestion);
  const resetGame = useCollectionsStore((state) => state.resetGame);

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [correctIndex, setCorrectIndex] = useState<number | null>(null);
  const [explanation, setExplanation] = useState<string | undefined>(undefined);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [hotspot, setHotspot] = useState<{ point: { x: number; y: number }; hit: string | null; zones: any[] } | null>(null);
  
  const [hotspotAll, setHotspotAll] = useState<HotspotAllResult | null>(null);
  const [hotspotBusy, setHotspotBusy] = useState(false);
  const hotspotPending = useRef(false);
  const [hotspotError, setHotspotError] = useState<string | null>(null);

  const [lineResult, setLineResult] = useState<LineResult | null>(null);
  const [lineBusy, setLineBusy] = useState(false);
  const linePending = useRef(false);
  const [lineError, setLineError] = useState<string | null>(null);

  const [textResult, setTextResult] = useState<TextResult | null>(null);
  const [textBusy, setTextBusy] = useState(false);
  const textPending = useRef(false);
  const [textError, setTextError] = useState<string | null>(null);
  const [categoriesResult, setCategoriesResult] = useState<CategoriesResult | null>(null);
  const [categoriesBusy, setCategoriesBusy] = useState(false);
  const categoriesPending = useRef(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null);
  const [orderBusy, setOrderBusy] = useState(false);
  const orderPending = useRef(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  const [labelsResult, setLabelsResult] = useState<LabelsResult | null>(null);
  const [labelsBusy, setLabelsBusy] = useState(false);
  const labelsPending = useRef(false);
  const [labelsError, setLabelsError] = useState<string | null>(null);

  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [xpEarned, setXpEarned] = useState(0);

  const currentQuestion = session?.questions?.[session.currentIndex] || null;
  const progress = session
    ? { current: session.currentIndex + 1, total: session.questions.length }
    : { current: 0, total: 0 };

  useEffect(() => {
    if (id && moduleId && !startedRef.current) {
      startedRef.current = true;
      startModule(id, moduleId);
    }

    return () => {
      resetGame();
    };
  }, [id, moduleId, startModule, resetGame]);

  const handleAnswer = async (answerIndex: number) => {
    if (showResult || !currentQuestion || !session || !id || !moduleId) return;

    setSelectedAnswer(answerIndex);

    try {
      const result = await apiAnswerQuestion(
        id,
        moduleId,
        session.id,
        currentQuestion.id,
        answerIndex
      );

      setCorrectIndex(result.correctIndex);
      setExplanation(result.explanation);
      setIsCorrect(result.correct);
      setShowResult(true);
      metrikaGoal('answer', { course: id, module: moduleId, q: currentQuestion.id, ok: result.correct ? 1 : 0 });

      if (result.correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        setMaxStreak(Math.max(maxStreak, newStreak));
        
        const xpGain = 10 + Math.min(newStreak - 1, 5) * 2;
        setXpEarned(prev => prev + xpGain);
        
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
        
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } else {
        setStreak(0);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }

    } catch (err) {
      console.error('Error answering question:', err);
      answerQuestion(answerIndex);
    }
  };

  const handleHotspot = async (point: { x: number; y: number }) => {
    if (showResult || !currentQuestion || !session || !id || !moduleId) return;
    setSelectedAnswer(-1);
    try {
      const result = await apiAnswerQuestion(id, moduleId, session.id, currentQuestion.id, -1, point);
      setHotspot({ point, hit: result.hit ?? null, zones: result.zones ?? [] });
      setExplanation(result.explanation);
      setIsCorrect(result.correct);
      setShowResult(true);
      metrikaGoal('answer', { course: id, module: moduleId, q: currentQuestion.id, ok: result.correct ? 1 : 0 });
      if (result.correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        setMaxStreak(Math.max(maxStreak, newStreak));
        setXpEarned(prev => prev + 10 + Math.min(newStreak - 1, 5) * 2);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } else {
        setStreak(0);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }
    } catch (err) {
      console.error('Error answering hotspot:', err);
      setHotspot({ point, hit: null, zones: [] });
      setIsCorrect(false);
      setShowResult(true);
    }
  };

  const handleHotspotAll = async (points: HotspotPoint[]) => {
    if (showResult || hotspotPending.current || !currentQuestion || !session || !id || !moduleId) return;
    hotspotPending.current = true;
    setHotspotBusy(true);
    setHotspotError(null);
    try {
      const result = await apiAnswerQuestion(id, moduleId, session.id, currentQuestion.id, -1, undefined, undefined, undefined, points);
      setHotspotAll({ points, hits: result.hits ?? {}, missed: result.missed ?? [], stray: result.stray ?? 0, zones: result.zones ?? [] });
      setExplanation(result.explanation);
      setIsCorrect(result.correct);
      setShowResult(true);
      metrikaGoal('answer', { course: id, module: moduleId, q: currentQuestion.id, ok: result.correct ? 1 : 0 });
      if (result.correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        setMaxStreak(Math.max(maxStreak, newStreak));
        setXpEarned(prev => prev + 10 + Math.min(newStreak - 1, 5) * 2);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } else {
        setStreak(0);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }
    } catch (err) {
      console.error('Ошибка проверки зон:', err);
      setHotspotError('Не удалось проверить ответ. Попробуйте ещё раз.');
    } finally {
      hotspotPending.current = false;
      setHotspotBusy(false);
    }
  };

  const handleLabels = async (placements: Record<string, string>) => {
    if (showResult || labelsPending.current || !currentQuestion || !session || !id || !moduleId) return;
    labelsPending.current = true;
    setLabelsBusy(true);
    setLabelsError(null);
    try {
      const result = await apiAnswerQuestion(id, moduleId, session.id, currentQuestion.id, -1, undefined, placements);
      setLabelsResult({ placements, perZone: result.perZone ?? {}, zones: result.zones ?? [] });
      setExplanation(result.explanation);
      setIsCorrect(result.correct);
      setShowResult(true);
      metrikaGoal('answer', { course: id, module: moduleId, q: currentQuestion.id, ok: result.correct ? 1 : 0 });
      if (result.correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        setMaxStreak(Math.max(maxStreak, newStreak));
        setXpEarned(prev => prev + 10 + Math.min(newStreak - 1, 5) * 2);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } else {
        setStreak(0);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }
    } catch (err) {
      console.error('Ошибка проверки подписей:', err);
      setLabelsError('Не удалось проверить ответ. Попробуйте ещё раз.');
    } finally {
      labelsPending.current = false;
      setLabelsBusy(false);
    }
  };

  const handleText = async (value: string) => {
    if (showResult || textPending.current || !currentQuestion || !session || !id || !moduleId) return;
    textPending.current = true;
    setTextBusy(true);
    setTextError(null);
    try {
      const result = await apiAnswerQuestion(id, moduleId, session.id, currentQuestion.id, -1, undefined, undefined, undefined, undefined, undefined, undefined, value);
      setTextResult({ value, correct: result.correct, correctAnswer: result.correctAnswer });
      setExplanation(result.explanation);
      setIsCorrect(result.correct);
      setShowResult(true);
      metrikaGoal('answer', { course: id, module: moduleId, q: currentQuestion.id, ok: result.correct ? 1 : 0 });
      if (result.correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        setMaxStreak(Math.max(maxStreak, newStreak));
        setXpEarned(prev => prev + 10 + Math.min(newStreak - 1, 5) * 2);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } else {
        setStreak(0);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }
    } catch (err) {
      console.error('Ошибка проверки ответа:', err);
      setTextError('Не удалось проверить ответ. Попробуйте ещё раз.');
    } finally {
      textPending.current = false;
      setTextBusy(false);
    }
  };

  const handleCategories = async (assignment: Record<string, string>) => {
    if (showResult || categoriesPending.current || !currentQuestion || !session || !id || !moduleId) return;
    categoriesPending.current = true;
    setCategoriesBusy(true);
    setCategoriesError(null);
    try {
      const result = await apiAnswerQuestion(id, moduleId, session.id, currentQuestion.id, -1, undefined, undefined, undefined, undefined, undefined, assignment);
      setCategoriesResult({ assignment, perItem: result.perItem ?? {}, items: result.items ?? [] });
      setExplanation(result.explanation);
      setIsCorrect(result.correct);
      setShowResult(true);
      metrikaGoal('answer', { course: id, module: moduleId, q: currentQuestion.id, ok: result.correct ? 1 : 0 });
      if (result.correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        setMaxStreak(Math.max(maxStreak, newStreak));
        setXpEarned(prev => prev + 10 + Math.min(newStreak - 1, 5) * 2);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } else {
        setStreak(0);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }
    } catch (err) {
      console.error('Ошибка проверки категорий:', err);
      setCategoriesError('Не удалось проверить ответ. Попробуйте ещё раз.');
    } finally {
      categoriesPending.current = false;
      setCategoriesBusy(false);
    }
  };

  const handleOrder = async (order: string[]) => {
    if (showResult || orderPending.current || !currentQuestion || !session || !id || !moduleId) return;
    orderPending.current = true;
    setOrderBusy(true);
    setOrderError(null);
    try {
      const result = await apiAnswerQuestion(id, moduleId, session.id, currentQuestion.id, -1, undefined, undefined, order);
      setOrderResult({ order, correctOrder: result.correctOrder ?? [], perStep: result.perStep ?? {}, steps: result.steps ?? [] });
      setExplanation(result.explanation);
      setIsCorrect(result.correct);
      setShowResult(true);
      metrikaGoal('answer', { course: id, module: moduleId, q: currentQuestion.id, ok: result.correct ? 1 : 0 });
      if (result.correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        setMaxStreak(Math.max(maxStreak, newStreak));
        setXpEarned(prev => prev + 10 + Math.min(newStreak - 1, 5) * 2);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } else {
        setStreak(0);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }
    } catch (err) {
      console.error('Ошибка проверки порядка:', err);
      setOrderError('Не удалось проверить ответ. Попробуйте ещё раз.');
    } finally {
      orderPending.current = false;
      setOrderBusy(false);
    }
  };

  const handleLine = async (lineId: string) => {
    if (showResult || linePending.current || !currentQuestion || !session || !id || !moduleId) return;
    linePending.current = true;
    setLineBusy(true);
    setLineError(null);
    try {
      const result = await apiAnswerQuestion(id, moduleId, session.id, currentQuestion.id, -1, undefined, undefined, undefined, undefined, lineId);
      setLineResult({ lines: result.lines ?? [], chosen: result.chosen ?? lineId });
      setExplanation(result.explanation);
      setIsCorrect(result.correct);
      setShowResult(true);
      metrikaGoal('answer', { course: id, module: moduleId, q: currentQuestion.id, ok: result.correct ? 1 : 0 });
      if (result.correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        setMaxStreak(Math.max(maxStreak, newStreak));
        setXpEarned(prev => prev + 10 + Math.min(newStreak - 1, 5) * 2);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } else {
        setStreak(0);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }
    } catch (err) {
      console.error('Ошибка проверки линии:', err);
      setLineError('Не удалось проверить ответ. Попробуйте ещё раз.');
    } finally {
      linePending.current = false;
      setLineBusy(false);
    }
  };

  const handleNext = useCallback(() => {
    if (textResult) {
      answerQuestion(-1, undefined, isCorrect, undefined, undefined, undefined, undefined, undefined, textResult.value);
    } else if (categoriesResult) {
      answerQuestion(-1, undefined, isCorrect, undefined, undefined, undefined, undefined, categoriesResult.assignment);
    } else if (lineResult) {
      answerQuestion(-1, undefined, isCorrect, undefined, undefined, undefined, lineResult.chosen);
    } else if (orderResult) {
      answerQuestion(-1, undefined, isCorrect, undefined, orderResult.order);
    } else if (labelsResult) {
      answerQuestion(-1, undefined, isCorrect, labelsResult.placements);
    } else if (hotspotAll) {
      answerQuestion(-1, undefined, isCorrect, undefined, undefined, hotspotAll.points);
    } else if (hotspot) {
      answerQuestion(-1, hotspot.point, isCorrect);
    } else {
      answerQuestion(selectedAnswer || 0);
    }
    setTextResult(null);
    setTextError(null);
    setCategoriesResult(null);
    setCategoriesError(null);
    setLineResult(null);
    setLineError(null);
    setOrderResult(null);
    setOrderError(null);
    setLabelsResult(null);
    setLabelsError(null);
    setHotspot(null);
    setHotspotAll(null);
    setHotspotError(null);
    setSelectedAnswer(null);
    setShowResult(false);
    setCorrectIndex(null);
    setExplanation(undefined);
    setIsCorrect(false);
  }, [answerQuestion, selectedAnswer, hotspot, hotspotAll, labelsResult, orderResult, lineResult, categoriesResult, textResult, isCorrect]);

  const handleContinue = () => {
    navigate(`/collections/${id}`);
  };

  const handleRetry = () => {
    startedRef.current = false;
    setStreak(0);
    setMaxStreak(0);
    setXpEarned(0);
    resetGame();
    setTimeout(() => {
      if (id && moduleId) {
        startedRef.current = true;
        startModule(id, moduleId);
      }
    }, 100);
  };

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const handleExit = () => setShowExitConfirm(true);
  const handleExitConfirm = () => {
    setShowExitConfirm(false);
    resetGame();
    navigate(`/collections/${id}`);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 text-center max-w-sm w-full">
          <div className="text-5xl mb-3">😕</div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Ошибка</h2>
          <p className="text-[#626B73] text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate(`/collections/${id}`)}
            className="w-full py-2 bg-[#F7F7F4] text-[#252A2D] font-semibold rounded-lg"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  if (result) {
    if (!resultSentRef.current) {
      resultSentRef.current = true;
      metrikaGoal(result.passed ? 'module_passed' : 'module_failed', { course: id, module: moduleId, score: result.score });
      if ((result as any).certificate) metrikaGoal('certificate', { course: id, score: result.score });
    }
    return (
      <ResultScreen
        score={result.score}
        passed={result.passed}
        xpEarned={xpEarned || result.xpEarned}
        newBestScore={result.newBestScore}
        moduleCompleted={result.moduleCompleted}
        collectionCompleted={result.collectionCompleted}
        certificate={(result as any).certificate ?? null}
        correctCount={result.correctCount}
        totalCount={result.totalCount}
        maxStreak={maxStreak}
        collectionId={id}
        onContinue={handleContinue}
        onRetry={handleRetry}
      />
    );
  }

  if (!currentQuestion || !session) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F7F4] to-[#EEF3F1] flex flex-col">
      {showExitConfirm && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full shadow-xl">
            <p className="text-gray-900 text-center mb-6">Выйти? Прогресс не сохранится.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowExitConfirm(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium">Отмена</button>
              <button onClick={handleExitConfirm} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium">Выйти</button>
            </div>
          </div>
        </div>
      )}
      <Confetti show={showConfetti} />
      
      <ProgressHeader 
        current={progress.current} 
        total={progress.total}
        streak={streak}
        xpEarned={xpEarned}
        onExit={handleExit}
      />

      {(currentQuestion as any).type === 'number' ? (
        <NumberCard key={currentQuestion.id} question={currentQuestion as TextCardProps['question']}
          result={textResult} busy={textBusy} error={textError} onCheck={handleText} onReport={() => setShowReportModal(true)} />
      ) : (currentQuestion as any).type === 'free' ? (
        <FreeCard key={currentQuestion.id} question={currentQuestion as TextCardProps['question']}
          result={textResult} busy={textBusy} error={textError} onCheck={handleText} onReport={() => setShowReportModal(true)} />
      ) : (currentQuestion as any).type === 'categories' ? (
        <CategoriesCard key={currentQuestion.id} question={currentQuestion as unknown as CategoriesQuestion}
          result={categoriesResult} busy={categoriesBusy} error={categoriesError} onCheck={handleCategories}
          onReport={() => setShowReportModal(true)} />
      ) : (currentQuestion as any).type === 'line' ? (
        <LineCard key={currentQuestion.id} question={currentQuestion as unknown as LineQuestion}
          result={lineResult} busy={lineBusy} error={lineError} onCheck={handleLine}
          onReport={() => setShowReportModal(true)} />
      ) : (currentQuestion as any).type === 'order' ? (
        <OrderCard
          key={currentQuestion.id}
          question={currentQuestion as unknown as OrderQuestion}
          result={orderResult}
          busy={orderBusy}
          error={orderError}
          onCheck={handleOrder}
          onReport={() => setShowReportModal(true)}
        />
      ) : (currentQuestion as any).type === 'labels' ? (
        <LabelsCard
          key={currentQuestion.id}
          question={currentQuestion as unknown as LabelsQuestion}
          result={labelsResult}
          busy={labelsBusy}
          error={labelsError}
          onCheck={handleLabels}
          onReport={() => setShowReportModal(true)}
        />
      ) : (currentQuestion as any).type === 'hotspot' ? (
        <HotspotCard
          key={`hotspot-${session.currentIndex}`}
          mode={(currentQuestion as any).mode}
          allResult={hotspotAll}
          busy={hotspotBusy}
          error={hotspotError}
          onCheck={handleHotspotAll}
          question={currentQuestion.text}
          image={(currentQuestion as any).image}
          result={hotspot}
          showResult={showResult}
          isCorrect={isCorrect}
          onTap={handleHotspot}
          quizTopic={currentQuestion.quizTopic}
          questionKey={`q-${session.currentIndex}`}
          onReport={() => setShowReportModal(true)}
        />
      ) : (
      <QuestionCard
        question={currentQuestion.text}
        options={currentQuestion.options}
        selectedAnswer={selectedAnswer}
        correctAnswer={correctIndex}
        showResult={showResult}
        onAnswer={handleAnswer}
        quizTopic={currentQuestion.quizTopic}
        questionKey={`q-${session.currentIndex}`}
        onReport={() => setShowReportModal(true)}
      />
      )}

      {/* Карточка с кнопкой «Далее» обязана появляться и БЕЗ explanation:
          иначе вопрос без пояснения намертво блокирует прохождение
          (кейс telegram_money 24.08 — владелец застрял на первом вопросе) */}
      {showResult && (
        <ExplanationCard
          explanation={explanation || (isCorrect ? 'Верно!' : 'Правильный ответ отмечен выше.')}
          isCorrect={isCorrect}
          onNext={handleNext}
        />
      )}

      <ReportModal
        isOpen={showReportModal}
        questionText={currentQuestion.text}
        collectionId={id}
        moduleId={moduleId}
        userId={window.Telegram?.WebApp?.initDataUnsafe?.user?.id}
        onClose={() => setShowReportModal(false)}
      />
    </div>
  );
}
